import { arrayMove } from '@dnd-kit/sortable';
import { Sortable } from '@typings';

import { clamp } from './clamp';
import { isDefined } from './is';
import { isEmpty } from './isEmpty';

interface GetMinDepthArgs {
  nextItem: Sortable.FlattenedItem | undefined;
}

const getMinDepth = ({ nextItem }: GetMinDepthArgs) => {
  if (isDefined(nextItem)) {
    return nextItem.depth;
  }

  return 0;
};

interface GetMaxDepthArgs {
  activeItem: Sortable.FlattenedItem | undefined;
  previousItem: Sortable.FlattenedItem | undefined;
  maxAllowedDepth?: number;
}

export const getMaxDepth = ({ activeItem, previousItem, maxAllowedDepth = Number.MAX_VALUE }: GetMaxDepthArgs) => {
  if (!isDefined(previousItem) || !isDefined(activeItem)) {
    return 0;
  }

  const itemChildrenDepth = flattenTree([activeItem]).reduce((acc, item) => Math.max(acc, item.depth), 0);
  const maxDepth = maxAllowedDepth - itemChildrenDepth;

  return Math.min(maxDepth, previousItem.depth + 1);
};

interface GetProjectionArgs {
  items: Sortable.FlattenedItem[];
  activeId: Sortable.Id;
  overId: Sortable.Id;
  dragOffset: number;
  indentationWidth: number;
  maxAllowedDepth?: number;
}

export const getProjection = ({ items, activeId, overId, dragOffset, indentationWidth, maxAllowedDepth }: GetProjectionArgs) => {
  const getParentId = () => {
    if (depth === 0 || !previousItem) {
      return null;
    }

    if (depth === previousItem.depth) {
      return previousItem.parentId;
    }

    if (depth > previousItem.depth) {
      return previousItem.id;
    }

    const newParent = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find(item => item.depth === depth)?.parentId;

    return newParent ?? null;
  };

  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeItemIndex];

  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];
  const dragDepth = Math.round(dragOffset / indentationWidth);
  const projectedDepth = (activeItem?.depth ?? 0) + dragDepth;
  const maxDepth = getMaxDepth({
    activeItem,
    maxAllowedDepth,
    previousItem,
  });
  const minDepth = getMinDepth({ nextItem });
  const depth = clamp(minDepth, maxDepth)(projectedDepth);

  return { depth, maxDepth, minDepth, parentId: getParentId() };
};

export const flattenTree = (sortableItems: Sortable.Items) => {
  const flatten = (items: Sortable.Items, parentId: Sortable.Id | null = null, depth = 0): Sortable.FlattenedItem[] => {
    return items.flatMap((item, index) => [{ ...item, depth, index, parentId }, ...flatten(item.children ?? [], item.id, depth + 1)]);
  };

  return flatten(sortableItems);
};

export const buildTree = (flattenedItems: Sortable.FlattenedItem[]) => {
  const buildSubTree = (items: Sortable.FlattenedItem[], parentId: Sortable.Id | null): Sortable.Item[] => {
    return items
      .filter(item => item.parentId === parentId)
      .map(item => {
        const subTree = buildSubTree(items, item.id);

        return {
          id: item.id,
          label: item.label,
          ...(!isEmpty(subTree) && { children: subTree }),
        };
      });
  };

  return buildSubTree(flattenedItems, null);
};

export const removeChildrenOf = (items: Sortable.FlattenedItem[], ids: Sortable.Id[]) => {
  const isChildOrDescendantOf = (item: Sortable.FlattenedItem, parentIds: Sortable.Id[]): boolean => {
    if (!isDefined(item.parentId)) {
      return false;
    }

    if (parentIds.includes(item.parentId)) {
      return true;
    }

    const parentItem = items.find(it => it.id === item.parentId);

    return parentItem ? isChildOrDescendantOf(parentItem, parentIds) : false;
  };

  return items.filter(item => !isChildOrDescendantOf(item, ids));
};
